import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { getCategoryLabel } from "../utils/categoryUtils";
import { usePersistedFormState } from "../hooks/usePersistedFormState";
import { DAYS_OF_MONTH, REMIND_OPTIONS, CATEGORY_GRADIENT_COLORS } from "../constants";
import { formatDay, getSafeArray } from "../utils/common";
import { groupByCurrency } from "../utils/currency";

// ============================================================================
// HELPERS
// ============================================================================
const getCategories = (customCategories = []) =>
  (customCategories || []).map(c => ({ value: c.id, label: c.name }));

const getCategoryColor = (categoryValue, customCategories = []) => {
  const index = (customCategories || []).findIndex(c => c.id === categoryValue);
  return CATEGORY_GRADIENT_COLORS[index >= 0 ? index % CATEGORY_GRADIENT_COLORS.length : 0];
};

// ============================================================================
// COMPONENTE: CategoriesSection
// ============================================================================
const CategoriesSection = ({ categories, onAdd, onEdit, onSaveEdit, onCancelEdit, onDelete, pendingDelete, onConfirmDelete, onCancelDelete, reassignTo, setReassignTo, newName, setNewName, editingId, editName, setEditName }) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Categorías</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">{categories.length} de momento</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          placeholder="Nueva categoría..."
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-44"
        />
        <button
          onClick={onAdd}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          + Agregar
        </button>
      </div>

      {categories.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Necesitas al menos una para agregar plantillas.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {categories.map((cat, i) => {
            const chipColors = ["bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200", "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200", "bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200", "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"];
            const chipClass = chipColors[i % chipColors.length];
            return (
              <div key={cat.id} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg ${chipClass} group`}>
                {editingId === cat.id ? (
                  <>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { e.key === "Enter" && onSaveEdit(); e.key === "Escape" && onCancelEdit(); }}
                      className="bg-white/80 dark:bg-gray-800 px-2 py-1 rounded text-sm w-28 focus:outline-none focus:ring-1"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button onClick={onSaveEdit} className="p-1 hover:bg-black/10 rounded" aria-label="Guardar">✓</button>
                    <button onClick={onCancelEdit} className="p-1 hover:bg-black/10 rounded" aria-label="Cancelar">✕</button>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium">{cat.name}</span>
                    <button onClick={() => onEdit(cat)} className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-black/10 rounded transition-opacity" aria-label="Editar">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button onClick={() => onDelete(cat)} disabled={categories.length === 1} className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-black/10 rounded transition-opacity disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Eliminar">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pendingDelete && (
        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">Eliminar &quot;{pendingDelete.name}&quot;. Mover a:</p>
          <div className="flex gap-2 flex-wrap items-center">
            <select
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              className="px-3 py-2 border border-amber-300 dark:border-amber-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white text-sm"
            >
              {categories.filter(c => c.id !== pendingDelete.id).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button onClick={() => onConfirmDelete(pendingDelete.id, reassignTo)} className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg">Eliminar</button>
            <button onClick={onCancelDelete} className="px-3 py-2 text-gray-600 dark:text-gray-400 text-sm hover:underline">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: TemplateCard
// ============================================================================
const TemplateCard = ({ template, onEdit, onDelete, onToggleActive, formatAmount, customCategories = [] }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = () => setIsMobileView(!mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const showCollapsed = !isExpanded && isMobileView;
  const showExpanded = isExpanded || !isMobileView;

  return (
    <div className={`rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 ${
      !template.active ? "opacity-60" : ""
    }`}>
      {/* Header con categoría - clicable para expandir/colapsar */}
      <button
        type="button"
        onClick={() => isMobileView && setIsExpanded(!isExpanded)}
        className={`w-full bg-gradient-to-r ${getCategoryColor(template.category, customCategories)} px-5 py-3 flex items-center justify-between cursor-pointer hover:opacity-95 transition-opacity text-left`}
        aria-expanded={showExpanded}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-white">
            {getCategoryLabel(template.category, customCategories)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {template.active ? (
            <span className="px-2 py-1 bg-white/20 text-white text-xs font-medium rounded-full">
              Activo
            </span>
          ) : (
            <span className="px-2 py-1 bg-black/20 text-white text-xs font-medium rounded-full">
              Inactivo
            </span>
          )}
          <svg
            className={`w-5 h-5 text-white transition-transform md:hidden ${showExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Resumen cuando está colapsado (solo en móvil) */}
      {showCollapsed && (
        <button
          type="button"
          className="w-full px-5 py-3 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
          onClick={() => setIsExpanded(true)}
          aria-label="Expandir para ver detalles"
        >
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 truncate flex-1 mr-3">{template.name}</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded">
              {template.currency || "PEN"}
            </span>
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">{formatAmount(template.amount, undefined, template.currency || "PEN")}</span>
          </div>
        </button>
      )}

      {/* Contenido expandido (siempre en desktop) */}
      {showExpanded && (
      <div className="p-5">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">
          {template.name}
        </h3>

        <div className="space-y-3 mb-4">
          {/* Monto */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Monto</span>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded">
                {template.currency || "PEN"}
              </span>
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {formatAmount(template.amount, undefined, template.currency || "PEN")}
              </span>
            </div>
          </div>

          {/* Día de vencimiento */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Vencimiento
            </span>
            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
              Vence día {formatDay(template.dueDay)}
            </span>
          </div>

          {/* Recordatorio */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Recordatorio
            </span>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {template.remindDaysBefore === 0 
                ? "El mismo día" 
                : `${template.remindDaysBefore} ${template.remindDaysBefore === 1 ? "día" : "días"} antes`
              }
            </span>
          </div>
        </div>

        {/* Switch y acciones */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          {/* Switch Activo/Inactivo */}
          <button
            onClick={() => onToggleActive(template.id)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              template.active ? "bg-green-500" : "bg-gray-300"
            }`}
            aria-label={template.active ? "Desactivar" : "Activar"}
            aria-pressed={template.active}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                template.active ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>

          {/* Botones de acción */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(template)}
              className="px-3 py-1.5 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
              aria-label={`Editar ${template.name}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Editar
            </button>
            <button
              onClick={() => onDelete(template.id, template.name)}
              className="px-3 py-1.5 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
              aria-label={`Eliminar ${template.name}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Eliminar
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: TemplateModal
// ============================================================================
const TemplateModal = ({ isOpen, onClose, onSave, editingTemplate, categories = [], currency, enabledCurrencies = ["PEN", "USD"] }) => {
  const storageKey = editingTemplate ? `recurring_template_edit_${editingTemplate.id}` : "recurring_template_new";
  const defaultCat = categories.length > 0 ? categories[0].value : "";
  const defaultState = {
    name: "",
    amount: "",
    currency: "PEN",
    category: defaultCat,
    dueDay: 1,
    remindDaysBefore: 2,
    active: true
  };

  const [formData, setFormData, clearPersisted, wasRestored] = usePersistedFormState(storageKey, defaultState);
  const [errors, setErrors] = useState({});

  // Cargar datos si estamos editando, o reiniciar si abrimos "nuevo" sin datos restaurados
  useEffect(() => {
    if (!isOpen) return;
    if (wasRestored) return;
    if (editingTemplate) {
      setFormData({
        name: editingTemplate.name,
        amount: String(editingTemplate.amount),
        currency: editingTemplate.currency || "PEN",
        category: editingTemplate.category || (categories[0]?.value ?? ""),
        dueDay: editingTemplate.dueDay,
        remindDaysBefore: editingTemplate.remindDaysBefore,
        active: editingTemplate.active
      });
    } else {
      setFormData({
        name: "",
        amount: "",
        currency: "PEN",
        category: defaultCat,
        dueDay: 1,
        remindDaysBefore: 2,
        active: true
      });
    }
    setErrors({});
  }, [editingTemplate, isOpen, categories, wasRestored, defaultCat, setFormData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo al cambiar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    if (!formData.amount) {
      newErrors.amount = "El monto es obligatorio";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "El monto debe ser mayor a 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const templateData = {
      ...formData,
      amount: parseFloat(formData.amount),
      currency: formData.currency || "PEN",
      dueDay: parseInt(formData.dueDay),
      remindDaysBefore: parseInt(formData.remindDaysBefore)
    };

    clearPersisted();
    onSave(templateData);
  };

  const handleClose = () => {
    clearPersisted();
    setFormData({
      name: "",
      amount: "",
      currency: "PEN",
      category: defaultCat,
      dueDay: 1,
      remindDaysBefore: 2,
      active: true
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header del modal */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {editingTemplate ? "Editar recurrente" : "Nuevo recurrente"}
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                Configura un gasto que se repite cada mes
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              aria-label="Cerrar modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ej: Alquiler, Netflix, Gimnasio..."
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                }`}
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Monto y Moneda */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monto <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                    {CURRENCY_OPTIONS.find(opt => opt.code === formData.currency)?.symbol || "S/"}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    placeholder="0.00"
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                      errors.amount
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                    }`}
                  />
                </div>
                {errors.amount && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.amount}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Moneda <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.currency || "PEN"}
                    onChange={(e) => handleChange("currency", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                  >
                    {CURRENCY_OPTIONS.filter(opt => enabledCurrencies.includes(opt.code)).map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Categoría
              </label>
              <div className="relative">
                <select
                  value={formData.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                <svg 
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Grid para Día y Recordatorio */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Día de vencimiento */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Día de vencimiento
                </label>
                <div className="relative">
                  <select
                    value={formData.dueDay}
                    onChange={(e) => handleChange("dueDay", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                  >
                    {DAYS_OF_MONTH.map((day) => (
                      <option key={day} value={day}>
                        Día {formatDay(day)}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Recordar días antes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recordar antes
                </label>
                <div className="relative">
                  <select
                    value={formData.remindDaysBefore}
                    onChange={(e) => handleChange("remindDaysBefore", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                  >
                    {REMIND_OPTIONS.map((days) => (
                      <option key={days} value={days}>
                        {days === 0 ? "El mismo día" : `${days} ${days === 1 ? "día" : "días"} antes`}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Switch Activo */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Estado
                </label>
                <p className="text-xs text-gray-500">
                  {formData.active ? "Este gasto está activo" : "Este gasto está desactivado"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleChange("active", !formData.active)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  formData.active ? "bg-green-500" : "bg-gray-300"
                }`}
                aria-label="Toggle activo"
                aria-pressed={formData.active}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                    formData.active ? "translate-x-8" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Botones de acción */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t-2 border-gray-100">
              <button
                type="button"
                onClick={handleClose}
                className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                aria-label="Cancelar"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                aria-label="Guardar recurrente"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: RecurringExpenses
// ============================================================================
const RecurringExpenses = () => {
  const navigate = useNavigate();
  const { state, dispatch, formatDisplayAmount } = useControlCash();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);
  const [reassignToCategoryId, setReassignToCategoryId] = useState("");

  const templates = state.recurringTemplates;
  const customCategories = getSafeArray(state.customCategories);
  const enabledCurrencies = state.enabledCurrencies || ["PEN", "USD"];
  const activeTemplates = templates.filter(t => t.active);
  // Agrupar templates activos por moneda
  const templatesByCurrency = groupByCurrency(activeTemplates);

  const handleOpenModal = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  const handleSaveTemplate = (templateData) => {
    const patch = { 
      ...templateData, 
      amount: parseFloat(templateData.amount),
      currency: templateData.currency || "PEN"
    };
    if (editingTemplate) {
      dispatch({
        type: "UPDATE_RECURRING_TEMPLATE",
        payload: {
          id: editingTemplate.id,
          patch
        }
      });
      console.log("✏️ Plantilla editada:", editingTemplate.id);
    } else {
      dispatch({
        type: "CREATE_RECURRING_TEMPLATE",
        payload: patch
      });
      console.log("➕ Nueva plantilla creada");
    }
    handleCloseModal();
  };

  const handleDeleteTemplate = async (id, name) => {
    const ok = await confirm({
      title: "Eliminar plantilla",
      message: `¿Estás seguro de eliminar "${name}"?`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({
        type: "DELETE_RECURRING_TEMPLATE",
        payload: id
      });
      console.log("🗑️ Plantilla eliminada:", id);
    }
  };

  const handleToggleActive = (id) => {
    dispatch({
      type: "TOGGLE_RECURRING_TEMPLATE",
      payload: id
    });
    console.log("🔄 Estado cambiado para:", id);
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.warning("Escribe el nombre de la categoría");
      return;
    }
    if (customCategories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      toast.warning("Ya existe una categoría con ese nombre");
      return;
    }
    dispatch({ type: "CREATE_CUSTOM_CATEGORY", payload: { name } });
    setNewCategoryName("");
    toast.success(`Categoría "${name}" creada`);
  };

  const handleStartEditCategory = (cat) => {
    setEditingCategoryId(cat.id);
    setEditingCategoryName(cat.name);
  };

  const handleSaveEditCategory = () => {
    const name = editingCategoryName.trim();
    if (!name) return;
    dispatch({ type: "UPDATE_CUSTOM_CATEGORY", payload: { id: editingCategoryId, name } });
    setEditingCategoryId(null);
    setEditingCategoryName("");
    toast.success("Categoría actualizada");
  };

  const handleDeleteCategory = (cat) => {
    if (customCategories.length <= 1) {
      toast.warning("Necesitas al menos una categoría. Crea otra antes de eliminar.");
      return;
    }
    setPendingDeleteCategory(cat);
    const other = customCategories.find(c => c.id !== cat.id);
    if (other) setReassignToCategoryId(other.id);
  };

  const handleConfirmDeleteCategory = (catId, reassignTo) => {
    dispatch({ type: "DELETE_CUSTOM_CATEGORY", payload: { id: catId, reassignTo } });
    setPendingDeleteCategory(null);
    toast.success("Categoría eliminada");
  };

  const handleCancelDeleteCategory = () => {
    setPendingDeleteCategory(null);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <PageHeader 
          onBack={handleBack}
          title="Gastos recurrentes"
          subtitle="Crea gastos que se repiten cada mes y la app te recordará pagarlos."
        />

        {/* Resumen compacto de estado */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800/50">
            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-semibold text-green-700 dark:text-green-400">
              {activeTemplates.length} {activeTemplates.length === 1 ? "activo" : "activos"}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
            <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-400">
              {templates.filter(t => !t.active).length} {templates.filter(t => !t.active).length === 1 ? "desactivado" : "desactivados"}
            </span>
          </div>
        </div>

        {/* Totales por moneda - diseño destacado */}
        <div className="mb-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Total mensual estimado
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enabledCurrencies.map((currencyCode) => {
              const total = templatesByCurrency[currencyCode] || 0;
              const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currencyCode);
              const currencyLabel = currencyInfo?.label || currencyCode;
              const currencySymbol = currencyInfo?.symbol || currencyCode;
              
              // Colores diferentes por moneda
              const currencyColors = {
                PEN: "from-emerald-500 to-teal-600 border-emerald-400/20",
                USD: "from-blue-500 to-indigo-600 border-blue-400/20",
                COP: "from-yellow-500 to-orange-600 border-yellow-400/20",
                ARS: "from-sky-500 to-cyan-600 border-sky-400/20",
                CLP: "from-red-500 to-pink-600 border-red-400/20",
                VEF: "from-purple-500 to-violet-600 border-purple-400/20"
              };
              
              const gradientClass = currencyColors[currencyCode] || "from-gray-500 to-gray-600 border-gray-400/20";
              
              return (
                <div
                  key={currencyCode}
                  className={`bg-gradient-to-br ${gradientClass} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-xl font-bold text-white">
                          {currencySymbol}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-white/90 uppercase tracking-wide">
                        {currencyLabel}
                      </p>
                    </div>
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1">
                    {formatDisplayAmount(total, undefined, currencyCode)}
                  </p>
                  <p className="text-xs text-white/70">
                    {activeTemplates.filter(t => t.currency === currencyCode).length} {activeTemplates.filter(t => t.currency === currencyCode).length === 1 ? "gasto recurrente" : "gastos recurrentes"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Categorías personalizadas */}
        <CategoriesSection
          categories={customCategories}
          onAdd={handleAddCategory}
          onEdit={handleStartEditCategory}
          onSaveEdit={handleSaveEditCategory}
          onCancelEdit={() => { setEditingCategoryId(null); setEditingCategoryName(""); }}
          onDelete={handleDeleteCategory}
          pendingDelete={pendingDeleteCategory}
          onConfirmDelete={handleConfirmDeleteCategory}
          onCancelDelete={handleCancelDeleteCategory}
          reassignTo={reassignToCategoryId}
          setReassignTo={setReassignToCategoryId}
          newName={newCategoryName}
          setNewName={setNewCategoryName}
          editingId={editingCategoryId}
          editName={editingCategoryName}
          setEditName={setEditingCategoryName}
        />

        {/* Botón principal */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mis plantillas</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {templates.length} {templates.length === 1 ? "plantilla" : "plantillas"} registradas
            </p>
          </div>
          <button
            onClick={handleOpenModal}
            disabled={customCategories.length === 0}
            className={`px-5 py-3 font-semibold rounded-2xl shadow-lg transition-all flex items-center gap-2 ${
              customCategories.length === 0
                ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30"
            }`}
            aria-label="Crear nuevo recurrente"
            title={customCategories.length === 0 ? "Crea al menos una categoría primero" : ""}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Nuevo recurrente</span>
            <span className="sm:hidden">Nuevo</span>
          </button>
        </div>

        {/* Lista de plantillas */}
        {templates.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-8 text-center">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              No hay plantillas aún
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {customCategories.length === 0
                ? "Crea al menos una categoría en la sección de arriba, luego podrás agregar plantillas de gastos recurrentes."
                : "Crea tu primera plantilla de gasto recurrente"}
            </p>
            <button
              onClick={handleOpenModal}
              disabled={customCategories.length === 0}
              className={`px-4 py-2 text-sm font-semibold rounded-xl shadow-lg transition-all inline-flex items-center gap-2 ${
                customCategories.length === 0
                  ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-blue-500/25 hover:shadow-xl"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Crear primera plantilla
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEditTemplate}
                onDelete={handleDeleteTemplate}
                onToggleActive={handleToggleActive}
                formatAmount={formatDisplayAmount}
                customCategories={state.customCategories || []}
              />
            ))}
          </div>
        )}

        {/* Modal - key obliga a remontar al cambiar entre nuevo/editar, evitando datos cruzados */}
        <TemplateModal
          key={editingTemplate?.id ?? "new"}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          enabledCurrencies={state.enabledCurrencies || ["PEN", "USD"]}
          onSave={handleSaveTemplate}
          editingTemplate={editingTemplate}
          categories={getCategories(state.customCategories)}
          currency={state.currencySymbol ?? state.currency}
        />
      </div>
    </Layout>
  );
};

export default RecurringExpenses;
